import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search, Package, Loader2, ArrowLeft, Star, Eye, EyeOff, Truck, AlertTriangle,
  ChevronRight, Filter, Pencil, Image as ImageIcon, CreditCard,
} from "lucide-react";
import { articulosConfigApi, ArticuloConfig } from "../../lib/api";
import { useInicio } from "../../lib/permisos";
import { EditarArticuloModal } from "./EditarArticuloModal";

function formatUSD(n: string | number | null | undefined): string {
  if (n == null) return "—";
  const num = typeof n === "string" ? Number(n) : n;
  if (isNaN(num)) return "—";
  return new Intl.NumberFormat("es-VE", {
    style: "currency", currency: "USD", maximumFractionDigits: 2,
  }).format(num);
}

export function ArticulosConfigPage() {
  const inicio = useInicio();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [debSearch, setDebSearch] = useState("");
  const [seccion, setSeccion] = useState<string>("");
  const [filtro, setFiltro] = useState<"todos" | "menu" | "delivery">("todos");
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState<ArticuloConfig | null>(null);
  const limit = 50;

  useEffect(() => {
    const t = setTimeout(() => setDebSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const secciones = useQuery({
    queryKey: ["articulos-config", "secciones"],
    queryFn: () => articulosConfigApi.secciones(),
  });

  const lista = useQuery({
    queryKey: ["articulos-config", "list", debSearch, seccion, filtro, page],
    queryFn: () => articulosConfigApi.list({
      busqueda: debSearch || undefined,
      seccion: seccion || undefined,
      soloDelivery: filtro === "delivery",
      soloMenu: filtro === "menu",
      limit, offset: page * limit,
    }),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, dto }: { id: number; dto: any }) => articulosConfigApi.actualizar(id, dto),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["articulos-config"] }),
  });

  function toggle(art: ArticuloConfig, campo: "visibleMenu" | "disponibleDelivery" | "destacado" | "agotado") {
    updateMut.mutate({ id: art.articuloId, dto: { [campo]: !art[campo] } });
  }

  return (
    <div className="min-h-screen bg-ink-50">
      <div className="mx-auto max-w-7xl px-6 py-8 animate-fade-in">

        <div className="flex items-center justify-between mb-5">
          <Link to={inicio}
            className="inline-flex items-center gap-1.5 text-sm text-ink-500 hover:text-ink-800 transition-colors">
            <ArrowLeft className="size-4" />
            Dashboard
          </Link>
          <Link to="/pasarelas-pago"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-700 hover:text-brand-900">
            <CreditCard className="size-4" />
            Pasarelas de pago
          </Link>
        </div>

        {/* Header */}
        <header className="flex items-end justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="size-12 rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 text-white flex items-center justify-center shadow-sm">
              <Package className="size-6" />
            </div>
            <div>
              <h1 className="font-display text-3xl font-bold text-ink-800 leading-tight">Configuración de artículos</h1>
              <p className="text-sm text-ink-500 mt-0.5">
                Define qué artículos aparecen en el menú digital y cuáles están disponibles para delivery
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <BotonMarkupDelivery onAplicado={() => qc.invalidateQueries({ queryKey: ["articulos-config"] })} />
            {lista.data && (
              <div className="text-right">
                <div className="text-3xl font-display font-bold text-ink-800 leading-none">
                  {lista.data.total.toLocaleString("es-ES")}
                </div>
                <div className="text-xs text-ink-500 mt-1">artículos</div>
              </div>
            )}
          </div>
        </header>

        {/* Filtros */}
        <div className="card p-4 mb-4">
          <div className="flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-ink-400" />
              <input
                type="text" placeholder="Buscar por nombre o código…"
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(0); }}
                className="input pl-10 w-full"
              />
            </div>

            <select value={seccion} onChange={(e) => { setSeccion(e.target.value); setPage(0); }}
              className="input min-w-[160px]">
              <option value="">Todas las secciones</option>
              {secciones.data?.map((s) => (
                <option key={s.seccionNombre} value={s.seccionNombre}>
                  {s.seccionNombre} ({s.total})
                </option>
              ))}
            </select>

            <div className="flex gap-1 bg-ink-100 p-1 rounded-lg">
              <FilterBtn active={filtro === "todos"} onClick={() => { setFiltro("todos"); setPage(0); }}>
                Todos
              </FilterBtn>
              <FilterBtn active={filtro === "menu"} onClick={() => { setFiltro("menu"); setPage(0); }}>
                <Eye className="size-3.5" /> En menú
              </FilterBtn>
              <FilterBtn active={filtro === "delivery"} onClick={() => { setFiltro("delivery"); setPage(0); }}>
                <Truck className="size-3.5" /> Delivery
              </FilterBtn>
            </div>
          </div>
        </div>

        {/* Tabla */}
        <div className="card overflow-hidden">
          {lista.isLoading ? (
            <div className="p-12 flex items-center justify-center text-ink-400">
              <Loader2 className="size-5 animate-spin mr-2" /> Cargando…
            </div>
          ) : !lista.data || lista.data.items.length === 0 ? (
            <div className="p-12 text-center text-ink-400">
              <Package className="size-12 mx-auto mb-2 opacity-30" />
              <p>Sin artículos</p>
            </div>
          ) : (
            <>
              <table className="w-full text-sm">
                <thead className="bg-ink-50 border-b border-ink-200">
                  <tr className="text-ink-600 text-xs uppercase tracking-wide">
                    <th className="px-2 py-3 font-medium w-12"></th>
                    <th className="px-4 py-3 font-medium text-left">Artículo</th>
                    <th className="px-4 py-3 font-medium text-left">Sección</th>
                    <th className="px-4 py-3 font-medium text-right">Precio</th>
                    <th className="px-4 py-3 font-medium text-center">Menú</th>
                    <th className="px-4 py-3 font-medium text-center">Delivery</th>
                    <th className="px-4 py-3 font-medium text-center">Destacado</th>
                    <th className="px-4 py-3 font-medium text-center">Agotado</th>
                    <th className="px-2 py-3 font-medium w-16 text-center">Editar</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.data.items.map((a) => (
                    <tr key={a.articuloId} className="border-b border-ink-100 hover:bg-brand-50/30 transition-colors">
                      <td className="px-2 py-2">
                        <div className="size-10 rounded bg-ink-100 overflow-hidden flex items-center justify-center">
                          {a.fotoUrl ? (
                            <img src={a.fotoUrl} alt={a.nombre} className="size-full object-cover" />
                          ) : (
                            <ImageIcon className="size-4 text-ink-400" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-ink-800">{a.nombre}</div>
                        <div className="text-xs text-ink-500 font-mono">#{a.codArticulo}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-ink-600">{a.seccionNombre || "—"}</td>
                      <td className="px-4 py-3 text-right font-mono text-ink-700">{formatUSD(a.precioUsd)}</td>
                      <td className="px-4 py-3 text-center">
                        <Toggle on={a.visibleMenu} onClick={() => toggle(a, "visibleMenu")}
                          color="brand" loading={updateMut.isPending && updateMut.variables?.id === a.articuloId} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Toggle on={a.disponibleDelivery} onClick={() => toggle(a, "disponibleDelivery")}
                          color="emerald" loading={updateMut.isPending && updateMut.variables?.id === a.articuloId} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Toggle on={a.destacado} onClick={() => toggle(a, "destacado")}
                          color="amber" loading={updateMut.isPending && updateMut.variables?.id === a.articuloId} />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <Toggle on={a.agotado} onClick={() => toggle(a, "agotado")}
                          color="red" loading={updateMut.isPending && updateMut.variables?.id === a.articuloId} />
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button onClick={() => setEditing(a)}
                          className="p-1.5 rounded hover:bg-brand-100 text-brand-700 transition-colors">
                          <Pencil className="size-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="flex items-center justify-between px-4 py-3 bg-ink-50 border-t border-ink-200">
                <span className="text-xs text-ink-500">
                  Mostrando {page * limit + 1}-{Math.min((page + 1) * limit, lista.data.total)} de {lista.data.total.toLocaleString("es-ES")}
                </span>
                <div className="flex gap-2">
                  <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}
                    className="btn-secondary disabled:opacity-50">Anterior</button>
                  <button onClick={() => setPage((p) => p + 1)} disabled={(page + 1) * limit >= lista.data.total}
                    className="btn-secondary disabled:opacity-50">Siguiente</button>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Modal editar */}
        <EditarArticuloModal articulo={editing} open={!!editing} onClose={() => setEditing(null)} />
      </div>
    </div>
  );
}

function FilterBtn({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button onClick={onClick}
      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all
        ${active ? "bg-white shadow-sm text-brand-700" : "text-ink-600 hover:text-ink-800"}`}>
      {children}
    </button>
  );
}

function Toggle({ on, onClick, color = "brand", loading }: {
  on: boolean; onClick: () => void; color?: "brand" | "emerald" | "amber" | "red"; loading?: boolean;
}) {
  const cs = {
    brand: { on: "bg-brand-600", off: "bg-ink-300" },
    emerald: { on: "bg-emerald-600", off: "bg-ink-300" },
    amber: { on: "bg-amber-500", off: "bg-ink-300" },
    red: { on: "bg-red-500", off: "bg-ink-300" },
  }[color];
  return (
    <button onClick={onClick} disabled={loading}
      className={`relative w-9 h-5 rounded-full transition-colors ${on ? cs.on : cs.off} disabled:opacity-50`}>
      <span className={`absolute top-0.5 left-0.5 size-4 bg-white rounded-full shadow transition-transform
        ${on ? "translate-x-4" : "translate-x-0"}`} />
      {loading && <Loader2 className="absolute inset-0 m-auto size-3 text-white animate-spin" />}
    </button>
  );
}

// ────────────────────────────────────────────────────────────
// Botón Markup masivo de precios delivery
// ────────────────────────────────────────────────────────────
function BotonMarkupDelivery({ onAplicado }: { onAplicado: () => void }) {
  const [abierto, setAbierto] = useState(false);
  const [pct, setPct] = useState("15");
  const [soloVisibles, setSoloVisibles] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [resultado, setResultado] = useState<string | null>(null);

  const aplicar = async () => {
    if (!confirm(`¿Aplicar +${pct}% sobre el precio de POS a TODOS los artículos${soloVisibles ? " visibles en delivery" : ""}?`)) return;
    setEnviando(true); setResultado(null);
    try {
      const r = await articulosConfigApi.bulkMarkupDelivery(Number(pct), { soloVisiblesDelivery: soloVisibles });
      setResultado(`✅ ${r.actualizados} artículos actualizados`);
      onAplicado();
    } catch (e: any) {
      setResultado(`❌ ${e?.message ?? "Error"}`);
    } finally { setEnviando(false); }
  };

  const resetear = async () => {
    if (!confirm("¿Eliminar el sobreprecio? Los precios volverán a coincidir con el POS.")) return;
    setEnviando(true); setResultado(null);
    try {
      const r = await articulosConfigApi.bulkMarkupDelivery(0, { reset: true });
      setResultado(`✅ Reset aplicado (${r.actualizados})`);
      onAplicado();
    } catch (e: any) {
      setResultado(`❌ ${e?.message ?? "Error"}`);
    } finally { setEnviando(false); }
  };

  if (!abierto) {
    return (
      <button onClick={() => setAbierto(true)}
        className="px-3 py-2 rounded-xl border-2 border-ink-200 hover:border-ink-400 text-sm text-ink-700 font-medium transition flex items-center gap-1.5">
        💰 Markup precios delivery
      </button>
    );
  }
  return (
    <div className="bg-white border-2 border-emerald-200 rounded-xl p-3 shadow-lg min-w-[320px]">
      <div className="flex items-center justify-between mb-2">
        <p className="font-semibold text-ink-800 text-sm">Markup masivo de precios delivery</p>
        <button onClick={() => setAbierto(false)} className="text-ink-400 hover:text-ink-700 text-xs">✕</button>
      </div>
      <div className="flex items-end gap-2 mb-2">
        <div className="flex-1">
          <label className="block text-[10px] uppercase text-ink-500 font-semibold mb-1">% sobre precio POS</label>
          <input type="number" value={pct} onChange={(e) => setPct(e.target.value)}
            className="w-full border-2 border-ink-200 rounded-lg px-2 py-1.5 text-sm font-mono"
            placeholder="15" min="-100" max="200" />
        </div>
        <button onClick={aplicar} disabled={enviando}
          className="px-4 py-1.5 bg-emerald-600 text-white rounded-lg text-sm font-semibold hover:bg-emerald-700 disabled:opacity-50">
          {enviando ? "..." : "Aplicar"}
        </button>
      </div>
      <label className="flex items-center gap-2 text-xs text-ink-600 mb-2">
        <input type="checkbox" checked={soloVisibles} onChange={(e) => setSoloVisibles(e.target.checked)}
          className="size-3.5 accent-emerald-600" />
        Solo a los artículos con delivery activo
      </label>
      <button onClick={resetear} disabled={enviando}
        className="text-xs text-rose-600 hover:underline">
        Quitar markup (volver al precio POS)
      </button>
      {resultado && (
        <p className="text-xs mt-2 text-ink-700 bg-ink-50 px-2 py-1 rounded">{resultado}</p>
      )}
    </div>
  );
}
